import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowUp, 
  faArrowDown, 
  faChartLine 
} from '@fortawesome/free-solid-svg-icons';
import { ThemeContext } from '../context/ThemeProvider';
import { getPracticeTestIconBg } from '../utils/themeUtils';
import { createLogger } from "../utils/logger";
import { AuthContext } from "../context/AuthProvider";

const ReadinessScoreImpact = ({ lastReadinessScoreInt, profileReadinessScoreInt }) => {
  const { loggingEnabled } = useContext(AuthContext);
  const logger = createLogger(loggingEnabled, 'ReadinessScoreImpact');
  logger.log("ReadinessScoreImpact component:", lastReadinessScoreInt, profileReadinessScoreInt);
  const { theme, currentTheme } = useContext(ThemeContext);



  // Helper function to get icon background color
  const getIconBg = () => {
    return getPracticeTestIconBg(theme, currentTheme);
  };

  // Helper function for text color
  const getTextColor = () => {
    return currentTheme === 'light' 
      ? theme?.text?.light
      : theme?.text?.dark;
  };

  const isIncrease = lastReadinessScoreInt < profileReadinessScoreInt;
  const difference = Math.abs(profileReadinessScoreInt - lastReadinessScoreInt);
  
  // Animation variants
  const containerVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.3,
        type: "spring",
        stiffness: 300,
        damping: 20
      }
    }
  };

  // Colors based on score change
  const getChangeColor = () => {
    if (lastReadinessScoreInt === profileReadinessScoreInt) {
      return theme?.color.gray.main;
    }
    return isIncrease ? theme?.color.green.main : theme?.color.red.main;
  };

  return (
    <motion.div
      initial="initial"
      animate="animate"
      variants={containerVariants}
      className="p-4 rounded-lg"
      style={{ 
        backgroundColor: currentTheme === 'light' ? theme?.background?.light : theme?.background?.dark
      }}
    >
      <div className="flex items-center">
        <div 
          className="w-12 h-12 rounded-full flex items-center justify-center mr-4"
          style={{ backgroundColor: getIconBg() }}
        >
          <FontAwesomeIcon 
            icon={faChartLine} 
            className="text-white text-lg" 
          />
        </div>
        
        <div className="flex-1">
          <h3 
            className="text-lg font-semibold mb-1"
            style={{ color: getTextColor() }}
          >
            Readiness Score Impact
          </h3>
          
          <div className="flex items-center">
            {lastReadinessScoreInt !== profileReadinessScoreInt && (
              <div 
                className="flex items-center justify-center h-6 w-6 mr-2 rounded-full"
                style={{ 
                  backgroundColor: getChangeColor(),
                  color: 'white'
                }}
              >
                <FontAwesomeIcon 
                  icon={isIncrease ? faArrowUp : faArrowDown} 
                  className="text-xs" 
                />
              </div>
            )}
            
            <p 
              className="font-medium"
              style={{ color: getChangeColor() }}
            >
              {lastReadinessScoreInt === profileReadinessScoreInt
                ? `Your overall readiness score remains unchanged at ${profileReadinessScoreInt}%`
                : isIncrease 
                  ? `Your overall readiness score increased by ${difference}% to ${profileReadinessScoreInt}%`
                  : `Your overall readiness score decreased by ${difference}% to ${profileReadinessScoreInt}%`
              }
            </p>
          </div>
        </div>
      </div>
      
      {/* Score Visualization */}
      <div className="mt-4 pl-16">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p 
              className="text-sm text-gray-500 dark:text-gray-400 mb-1"
            >
              Previous Score
            </p>
            <div className="flex items-center">
              <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: currentTheme === 'light' ? theme?.homeCard?.end_light : theme?.homeCard?.end_dark }}>
                <div 
                  className="h-full"
                  style={{ 
                    width: `${lastReadinessScoreInt}%`,
                    backgroundColor: getChangeColor()
                  }}
                ></div>
              </div>
              <span 
                className="ml-2 font-medium text-sm"
                style={{ color: getTextColor() }}
              >
                {lastReadinessScoreInt}%
              </span>
            </div>
          </div>
          
          <div>
            <p 
              className="text-sm text-gray-500 dark:text-gray-400 mb-1"
            >
              Current Score
            </p>
            <div className="flex items-center">
              <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: currentTheme === 'light' ? theme?.homeCard?.end_light : theme?.homeCard?.end_dark }}>
                <div 
                  className="h-full"
                  style={{ 
                    width: `${profileReadinessScoreInt}%`,
                    backgroundColor: getChangeColor()
                  }}
                ></div>
              </div>
              <span 
                className="ml-2 font-medium text-sm"
                style={{ color: getChangeColor() }}
              >
                {profileReadinessScoreInt}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ReadinessScoreImpact;